import React, { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Result, Spin } from "antd";
import LandToursApi from "../../../api-services/land-tours-api.service";
import { LandTourCategory, LandTourDetail } from "../../../interfaces/land-tour.interface";
import GroundTourHeader from "./components/GroundTourHeader";
import GroundTourPromotions from "./components/GroundTourPromotions";
import GroundTourGallery from "./components/GroundTourGallery";
import WhatsIncluded from "./components/WhatsIncluded";
import HighlightCard from "./components/HighlightCard";
import AboutTheTour from "./components/AboutTheTour";
import ChooseYourTour, { DateSelection, EMPTY_PAX_COUNTS, PaxCounts, defaultCategory, defaultPaxCounts, carriedDateSelection, displayPriceWindow, headcount, seatsAvailable, totalPax } from "./components/ChooseYourTour";
import GroundTourItinerary from "./components/GroundTourItinerary";
import GroundTourSidebar from "./components/GroundTourSidebar";
import GroundTourPriceBar from "./components/GroundTourPriceBar";
import useBreakpoint from "../../../utils/useBreakpoint";
import BookingNoticeTab from "../BookingNoticeTab";
import GroundTourBookingFlow from "./GroundTourBookingFlow";
import { customizeTourHref } from "./customizeTourHref";
import { INVALID_ROOM_MESSAGE, roomConfigurations } from "./booking/bookingUtils";
import useBookingQuote from "./booking/useBookingQuote";
import PriceBreakdownModal from "./booking/PriceBreakdownModal";

// Ground Tour Itinerary Show Page (issue #629). Mirrors the Series show page's
// data-fetch shape (SeriesApi.getItineraryV2) via LandTourApi, but is kept as a
// thin composition of section components rather than ShowPage.tsx's single
// 2,500+ line file - there is no v1/v2/v3 layout-version split to support here.
export default function GroundTourShowPage() {
  const { code } = useParams<{ code: string }>();
  const isMobile = useBreakpoint();
  const [searchParams] = useSearchParams();
  const [landTour, setLandTour] = useState<LandTourDetail | null>(null);
  const [loading, setLoading] = useState(true);

  const [selectedCategory, setSelectedCategory] = useState<LandTourCategory | undefined>(undefined);
  const [selectedDate, setSelectedDate] = useState<DateSelection | undefined>(undefined);
  const [paxCounts, setPaxCounts] = useState<PaxCounts>(EMPTY_PAX_COUNTS);
  // Distinct from `!landTour` alone so the not-found state only shows after a real fetch
  // attempt - a missing/empty :code param renders nothing rather than a false "not found".
  const [loadFailed, setLoadFailed] = useState(false);
  const [dateError, setDateError] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [breakdownOpen, setBreakdownOpen] = useState(false);

  useEffect(() => {
    if (!code) {
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    setLoadFailed(false);

    LandToursApi
      .getLandTour(code, searchParams.get("ref"))
      .then((data) => {
        if (!active) return;
        setLandTour(data);

        // The listing's category and the minimum headcount are preselected; the customer picks the date.
        setSelectedCategory(defaultCategory(data));
        setSelectedDate(undefined);
        setPaxCounts(defaultPaxCounts(data));
        setDateError(false);
      })
      .catch(() => {
        if (!active) return;
        setLandTour(null);
        setLoadFailed(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const handleCategoryChange = (category: LandTourCategory) => {
    // Keep the customer's date and headcount so they can compare category prices; the date only
    // carries over when the new category also departs that day (with its own price window).
    const carried = carriedDateSelection(category, selectedDate, {
      bookingCutOffDay: landTour?.bookingCutOffDay,
      departDay: landTour?.departDay,
      minimumPax: Math.max(landTour?.minBooking || 1, 1),
    });
    setSelectedCategory(category);
    setSelectedDate(carried);
    if (!carried && selectedDate) setDateError(true);
  };

  const seatsLeft = seatsAvailable(selectedCategory, selectedDate?.date, selectedDate?.priceDate);
  const selectionComplete = !!selectedCategory && !!selectedDate &&
    totalPax(paxCounts) >= Math.max(landTour?.minBooking || 1, 1) &&
    (seatsLeft === undefined || headcount(paxCounts) <= seatsLeft);

  // Travellers that cannot share rooms (e.g. 1 adult, 1 child without bed, 1 infant) cannot be booked.
  const roomInvalid = totalPax(paxCounts) > 0 && roomConfigurations(paxCounts).length === 0;

  const handleBookingCta = () => {
    if (roomInvalid) {
      document.querySelector(".ground-tour-show-v2__travellers")?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    if (!selectionComplete || !selectedCategory || !selectedDate) {
      // Scrolls to the Tour Category title (the top of the section) and flags the missing date.
      setDateError(!selectedDate);
      document.querySelector(".ground-tour-show-v2__choose")?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    setBookingOpen(true);
  };

  const closeBooking = () => {
    setBookingOpen(false);
    window.scrollTo({ top: 0 });
  };

  // Price card inputs shared by the desktop sidebar and the mobile price bar. Until a date is picked the card
  // quotes the category's headline (listing) price from its highest-priced window.
  const priceWindow = selectedDate?.priceDate || displayPriceWindow(selectedCategory);
  const contactHref = customizeTourHref({
    tourCode: landTour?.code,
    destination: landTour?.country,
    departDate: selectedDate?.date,
  });
  const { quote: totalQuote } = useBookingQuote({
    landTourId: landTour?.id,
    categoryId: selectedCategory?.id,
    departDate: selectedDate?.date,
    paxCounts,
    totalRooms: roomConfigurations(paxCounts)[0]?.total || 0,
    enabled: selectionComplete,
  });
  const priceCardProps = {
    landTourId: landTour?.id,
    selectedCategory,
    priceWindow,
    selectedDate,
    paxCounts,
    compulsoryCharges: landTour?.compulsoryCharges,
    complete: selectionComplete,
    total: totalQuote?.total,
    contactHref,
    bookingCtaLabel: (selectionComplete ? "Next" : "Select Tour") as "Select Tour" | "Next",
    onBookingCta: handleBookingCta,
    onShowBreakdown: () => setBreakdownOpen(true),
  };

  const breakdownModal = landTour && selectedDate && (
    <PriceBreakdownModal
      open={breakdownOpen}
      onClose={() => setBreakdownOpen(false)}
      landTour={landTour}
      priceDate={selectedDate.priceDate}
      paxCounts={paxCounts}
      quote={totalQuote}
    />
  );

  if (loading) {
    return (
      <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!landTour) {
    if (!loadFailed) return null;

    return (
      <Result
        status="404"
        title="Tour not found"
        subTitle="This tour may no longer be available, or the link may be incorrect."
        extra={<Link to="/web/ground-tour">Back to Ground Tours</Link>}
      />
    );
  }

  // The booking flow takes over the page (site header/footer stay) rather than overlaying it; the
  // selection lives here, so Back/Cancel returns to the show page exactly as the customer left it.
  if (bookingOpen && selectedCategory && selectedDate) {
    return (
      <GroundTourBookingFlow
        landTour={landTour}
        category={selectedCategory}
        selectedDate={selectedDate}
        paxCounts={paxCounts}
        onClose={closeBooking}
        onSelectionChange={(category, date, counts) => {
          setSelectedCategory(category);
          setSelectedDate(date);
          setPaxCounts(counts);
        }}
      />
    );
  }

  if (isMobile) {
    return (
      <div id="ground-tour-show-v2" className="ground-tour-show-v2--mobile">
        <GroundTourGallery images={landTour.images} />

        <div className="ground-tour-show-v2__m-body">
          <div className="ground-tour-show-v2__m-intro">
            <GroundTourHeader landTour={landTour} />
            <GroundTourPromotions landTour={landTour} />
            <WhatsIncluded landTour={landTour} />
            <HighlightCard highlight={landTour.highlight} />
            <AboutTheTour landTour={landTour} />
          </div>

          <ChooseYourTour
            landTour={landTour}
            selectedCategory={selectedCategory}
            onCategoryChange={handleCategoryChange}
            selectedDate={selectedDate}
            onDateChange={setSelectedDate}
            paxCounts={paxCounts}
            onPaxCountsChange={setPaxCounts}
            dateError={dateError}
            travellersError={roomInvalid ? INVALID_ROOM_MESSAGE : undefined}
          />

          <GroundTourItinerary landTour={landTour} />

          <section className="ground-tour-show-v2__booking-notice">
            <h2 className="ground-tour-show-v2__section-title">Booking Notice</h2>
            <BookingNoticeTab removeHeader grouped />
          </section>
        </div>

        <GroundTourPriceBar {...priceCardProps} />
        {breakdownModal}
      </div>
    );
  }

  return (
    <div id="ground-tour-show-v2">
      <GroundTourHeader landTour={landTour} />

      <div className="ground-tour-show-v2__gallery-wrap">
        <GroundTourGallery images={landTour.images} />
      </div>

      <div className="ground-tour-show-v2__layout">
        <div className="ground-tour-show-v2__main">
          <GroundTourPromotions landTour={landTour} />
          <WhatsIncluded landTour={landTour} />
          <HighlightCard highlight={landTour.highlight} />
          <AboutTheTour landTour={landTour} />
          <ChooseYourTour
            landTour={landTour}
            selectedCategory={selectedCategory}
            onCategoryChange={handleCategoryChange}
            selectedDate={selectedDate}
            onDateChange={setSelectedDate}
            paxCounts={paxCounts}
            onPaxCountsChange={setPaxCounts}
            dateError={dateError}
            travellersError={roomInvalid ? INVALID_ROOM_MESSAGE : undefined}
          />
          <GroundTourItinerary landTour={landTour} />

          <section className="ground-tour-show-v2__booking-notice">
            <h2 className="ground-tour-show-v2__section-title">Booking Notice</h2>
            {/* removeHeader: the section heading above already says "Booking Notice". */}
            <BookingNoticeTab removeHeader grouped />
          </section>
        </div>

        <GroundTourSidebar {...priceCardProps} />
        {breakdownModal}
      </div>
    </div>
  );
}
